import { useEffect, useMemo } from 'react'
import {
  SaturatedItem,
  selectLicensedFeatures,
  selectTreeFirstNode,
  useCheckAccess,
  useGetUsersPermissionsQuery,
} from 'react-common'
import { useNavigate } from 'react-router-dom'
import { hasChatHash } from '@/features/chat/chatHash'
import { useAppSelector } from '@/store/hooks'
import { captureException } from '@/utils/sentryUtils'
import { lmsCloudRedirects } from '@/features/stencil/StencilPreset/hooks/useCommonNavigationItems'

export const useCheckLearnerAccess = (navigationStructureLoading: boolean) => {
  const { isLoading: isPermissionsLoading } = useGetUsersPermissionsQuery()

  const firstNode = useAppSelector<SaturatedItem | null>(selectTreeFirstNode)
  const licensedFeatures = useAppSelector<string[]>(selectLicensedFeatures)

  const { checkAccess } = useCheckAccess({})
  const navigate = useNavigate()

  const shouldRedirectToPlatform = useMemo(() => {
    const isLMSLicense = (license: string) => /LMS/.test(license)
    const hasLMSLicense = licensedFeatures.some((license) => isLMSLicense(license))

    const hasResearchOrKnowledgeModelsOnly =
      licensedFeatures.length > 0 &&
      licensedFeatures.every((license) => license === 'Platform.Research' || license === 'Platform.KnowledgeModels')

    return hasResearchOrKnowledgeModelsOnly && !hasLMSLicense
  }, [licensedFeatures])

  useEffect(() => {
    if (isPermissionsLoading || navigationStructureLoading) {
      return
    }

    if (shouldRedirectToPlatform) {
      if (lmsCloudRedirects()) {
        console.log('Redirecting to Cloud')
        window.location.href = '/cloud/fragments/platform/platformMain?returnUrl=/'
      }

      console.log('Redirecting to platform')
      window.location.href = '/platform'
      return
    }

    const hasAccessToLearner = checkAccess([{ name: 'ObjectiveWorkflow.Create' }])

    if (hasAccessToLearner) {
      return
    }

    if (!firstNode) {
      navigate('/forbidden')
      return
    }

    if (typeof firstNode === 'string') {
      return
    }

    if (!firstNode.url) {
      captureException('[useCheckLearnerAccess] there is no url in the firstNode')
      return
    }

    // If we have chat hash in the url, we should keep it.
    // Because in portal pages we have chat feature and user should open chat if user comes from email
    if (hasChatHash()) {
      const urlHash = window.location.hash
      window.location.href = `${firstNode.url}${urlHash}`
      return
    }

    window.location.href = firstNode.url
  }, [firstNode, isPermissionsLoading, shouldRedirectToPlatform, navigationStructureLoading, checkAccess, navigate])
}
